/**
 * ============================================
 * CART GOAL PROGRESS SCRIPT (OPTIMIZED)
 * ============================================
 * IMPORTANT: Add this file to your assets folder
 * Then load it in theme.liquid with defer:
 * <script src="{{ 'cart-goal-progress.js' | asset_url }}" defer></script>
 *
 * OPTIMIZATIONS:
 * - Polling reduced from 500ms to 2000ms (4x fewer network requests)
 * - Removed duplicate DOMContentLoaded listeners
 * - Extracted from inline script to cacheable asset file
 * ============================================
 */

(function() {
  'use strict';

  const STRAPLESS_BRA_HANDLE = 'free-strapless-push-up-bra';
  const LAUNDRY_BAG_HANDLE = 'free-delicate-laundry-bag';
  const POLLING_INTERVAL = 5000;

  let productCollectionCache = {};
  let lastState = null;
  let straplessBraInCart = false;
  let laundryBagInCart = false;

  function isBodysuit(item) {
    if (productCollectionCache.hasOwnProperty(item.id)) {
      return productCollectionCache[item.id];
    }

    const tags = (item.tags || []).map(t => t.toLowerCase());
    if (tags.some(tag => tag.includes('bodysuit-collection'))) {
      productCollectionCache[item.id] = true;
      return true;
    }

    const type = (item.product_type || '').toLowerCase();
    if (type.includes('bodysuit')) {
      productCollectionCache[item.id] = true;
      return true;
    }

    const handle = (item.handle || '').toLowerCase();
    if (handle.includes('bodysuit')) {
      productCollectionCache[item.id] = true;
      return true;
    }

    const title = (item.title || '').toLowerCase();
    if (title.includes('bodysuit')) {
      productCollectionCache[item.id] = true;
      return true;
    }

    productCollectionCache[item.id] = false;
    return false;
  }

  function updateProductCollections(items) {
    items.forEach(item => isBodysuit(item));
  }

  // --- Notification helpers ---
  // Always show/hide based on current eligibility. No "previously unlocked"
  // gating — the cart drawer DOM can be replaced at any time by cart:refresh,
  // so we must be able to re-show notifications from scratch.

  function ensureNotificationVisible(card) {
    if (!card) return;
    if (card.classList.contains('active')) return; // already showing
    card.style.display = 'block';
    card.offsetHeight; // force reflow for CSS transition
    card.classList.add('active');
  }

  function ensureNotificationHidden(card) {
    if (!card) return;
    if (card.style.display === 'none' && !card.classList.contains('active')) return;
    card.classList.remove('active');
    card.addEventListener('transitionend', function handler() {
      card.removeEventListener('transitionend', handler);
      if (!card.classList.contains('active')) {
        card.style.display = 'none';
      }
    });
    setTimeout(function() {
      if (!card.classList.contains('active')) {
        card.style.display = 'none';
      }
    }, 350);
  }

  // --- Add to cart helpers ---

  function addDirectToCart(variantId, btn) {
    if (btn) btn.disabled = true;

    // Immediately hide the notification card (optimistic UI)
    var card = btn ? btn.closest('.free-gift-card') : null;
    if (card) {
      card.classList.remove('active');
      card.style.display = 'none';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
      return res.json();
    })
    .then(function() {
      // Reset lastState so next updateCartProgress actually runs
      lastState = null;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      // Wait for section re-render to finish before updating progress DOM.
      // cart:refresh triggers an async section re-render that replaces our
      // DOM elements — updating immediately would race with that.
      setTimeout(updateCartProgress, 800);
    })
    .catch(function() {
      // Re-show the notification on failure
      if (card) {
        card.style.display = 'block';
        card.offsetHeight;
        card.classList.add('active');
      }
    })
    .finally(function() { if (btn) btn.disabled = false; });
  }

  // --- Event listener binding ---
  // Called on init AND after every cart update (DOM may have been replaced).

  function bindGiftButtons() {
    // Tier 2 (strapless bra): "Add" toggles inline size picker
    var tier2Btn = document.getElementById('tier2-open-drawer-btn');
    if (tier2Btn && !tier2Btn._giftBound) {
      tier2Btn._giftBound = true;
      tier2Btn.addEventListener('click', function() {
        var picker = document.getElementById('tier2-size-picker');
        if (picker) {
          var isVisible = picker.style.display !== 'none';
          picker.style.display = isVisible ? 'none' : 'block';
        }
      });
    }

    // Tier 2 size buttons: each adds the selected variant to cart
    var sizePicker = document.getElementById('tier2-size-picker');
    if (sizePicker && !sizePicker._giftBound) {
      sizePicker._giftBound = true;
      sizePicker.addEventListener('click', function(e) {
        var sizeBtn = e.target.closest('.gift-size-picker__btn');
        if (!sizeBtn) return;
        var variantId = parseInt(sizeBtn.getAttribute('data-variant-id'), 10);
        if (variantId) {
          addDirectToCart(variantId, sizeBtn);
        }
      });
    }

    // Tier 3 (laundry bag): single variant, add directly
    var tier3Btn = document.getElementById('tier3-open-drawer-btn');
    if (tier3Btn && !tier3Btn._giftBound) {
      tier3Btn._giftBound = true;
      tier3Btn.addEventListener('click', function() {
        var variantId = tier3Btn.getAttribute('data-variant-id');
        if (variantId) {
          addDirectToCart(parseInt(variantId, 10), tier3Btn);
        }
      });
    }
  }

  // --- Exchange rate ---

  async function getExchangeRateToUSD() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      const activeCurrency = window.Shopify.currency.active;
      const rate = window.Shopify.currency.rate || 1;

      if (activeCurrency === 'USD') {
        return 1;
      }

      return rate;
    }

    return 1;
  }

  function convertToUSD(amountInLocalCurrency, exchangeRate) {
    if (exchangeRate && exchangeRate !== 1) {
      return amountInLocalCurrency / exchangeRate;
    }
    return amountInLocalCurrency;
  }

  // --- Main update loop ---

  function updateCartProgress() {
    fetch('/cart.json')
      .then(response => response.json())
      .then(async (cart) => {
        updateProductCollections(cart.items);

        let bodysuitCount = 0;
        straplessBraInCart = false;
        laundryBagInCart = false;

        const cartTotalLocal = cart.total_price / 100;
        const exchangeRate = await getExchangeRateToUSD();
        const cartTotal = convertToUSD(cartTotalLocal, exchangeRate);

        cart.items.forEach(item => {
          if (item.handle === STRAPLESS_BRA_HANDLE) {
            straplessBraInCart = true;
          }

          if (item.handle === LAUNDRY_BAG_HANDLE) {
            laundryBagInCart = true;
          }

          if (isBodysuit(item)) {
            bodysuitCount += item.quantity;
          }
        });

        const tier1 = true;
        const tier2 = cartTotal >= 90 && straplessBraInCart;
        const tier3 = cartTotal >= 120 && laundryBagInCart;
        const tier4 = false;

        const tier2Available = cartTotal >= 90;
        const tier3Available = cartTotal >= 120;

        const shouldShowTier2 = tier2Available && !straplessBraInCart;
        const shouldShowTier3 = tier3Available && !laundryBagInCart;

        let progress = 33;
        if (tier2) progress = 66;
        if (tier3) progress = 100;

        let currentTier = 1;
        if (tier3) currentTier = 3;
        else if (tier2) currentTier = 2;

        const newState = JSON.stringify({ bodysuitCount, cartTotal, tier1, tier2, tier3, tier4, progress, currentTier, straplessBraInCart, laundryBagInCart });

        if (newState === lastState) return;
        lastState = newState;

        // Re-bind buttons in case cart drawer DOM was replaced by cart:refresh
        bindGiftButtons();

        // Always show/hide notifications based on current eligibility
        if (shouldShowTier2) {
          ensureNotificationVisible(document.getElementById('tier2-gift-card'));
        } else {
          ensureNotificationHidden(document.getElementById('tier2-gift-card'));
        }

        if (shouldShowTier3) {
          ensureNotificationVisible(document.getElementById('tier3-gift-card'));
        } else {
          ensureNotificationHidden(document.getElementById('tier3-gift-card'));
        }

        let nextMilestoneText = '';
        if (cartTotal < 90) {
          const neededUSD = 90 - cartTotal;
          const neededLocal = Math.ceil(neededUSD * exchangeRate);
          const currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
          let formattedAmount;
          try {
            formattedAmount = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(neededLocal);
          } catch (e) {
            formattedAmount = '$' + neededLocal;
          }
          nextMilestoneText = `Spend ${formattedAmount} more to unlock free gifts!`;
        } else if (!straplessBraInCart) {
          nextMilestoneText = `Add the Free Strapless Bra to your cart!`;
        } else if (cartTotal < 120) {
          const neededUSD = 120 - cartTotal;
          const neededLocal = Math.ceil(neededUSD * exchangeRate);
          const currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
          let formattedAmount;
          try {
            formattedAmount = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(neededLocal);
          } catch (e) {
            formattedAmount = '$' + neededLocal;
          }
          nextMilestoneText = `Spend ${formattedAmount} more to unlock a Free Laundry Bag!`;
        } else if (!laundryBagInCart) {
          nextMilestoneText = `Add the Free Laundry Bag to your cart!`;
        } else {
          nextMilestoneText = '🎉 All rewards unlocked!';
        }

        updateProgressDOM(progress, currentTier, nextMilestoneText, { tier2, tier3, tier4, tier2Available, tier3Available, bodysuitCount });
      })
      .catch(() => {
        // Silent fail - cart fetch failed but we'll retry on next poll
      });
  }

  function updateProgressDOM(progress, currentTier, headline, tiers) {
    const progressEl = document.getElementById('cart-goal-progress');
    if (!progressEl) return;

    const headlineEl = progressEl.querySelector('.cart-goal-compact-headline');
    if (headlineEl) {
      headlineEl.textContent = headline;
    }

    const progressFill = progressEl.querySelector('.cart-goal-compact-progress-fill');
    if (progressFill) {
      progressFill.style.width = progress + '%';
    }

    const milestoneEls = progressEl.querySelectorAll('[data-tier]');
    milestoneEls.forEach((el, index) => {
      const tier = index + 2;
      const isUnlocked = (tier === 2 && tiers.tier2) ||
                        (tier === 3 && tiers.tier3) ||
                        (tier === 4 && tiers.tier4);

      const isAvailable = (tier === 2 && tiers.tier2Available) ||
                          (tier === 3 && tiers.tier3Available) ||
                          (tier === 4 && tiers.tier4);

      const marker = el.querySelector('[data-marker]');
      if (isUnlocked) {
        el.classList.add('active');
        marker.classList.add('unlocked');
      } else {
        el.classList.remove('active');
        marker.classList.remove('unlocked');
      }

      if (isAvailable) {
        el.classList.add('available');
      } else {
        el.classList.remove('available');
      }
    });
  }

  function initCartProgress() {
    bindGiftButtons();
    updateCartProgress();

    document.addEventListener('cart:updated', function() {
      bindGiftButtons();
      updateCartProgress();
    });
    document.addEventListener('cart-updated', function() {
      bindGiftButtons();
      updateCartProgress();
    });
    document.addEventListener('shopify:section:load', function() {
      bindGiftButtons();
      updateCartProgress();
    });
    // After cart:refresh triggers section re-render, the DOM is replaced.
    // Re-bind buttons and update progress after the re-render settles.
    document.addEventListener('cart:refresh', function() {
      setTimeout(function() {
        bindGiftButtons();
        lastState = null;
        updateCartProgress();
      }, 1000);
    });

    setInterval(updateCartProgress, POLLING_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initCartProgress();
    });
  } else {
    initCartProgress();
  }

})();
